import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Search, ArrowRight, Car, Sparkles, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function Landing() {
  const [reg, setReg] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [debugOtp, setDebugOtp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const check = async (e) => {
    e.preventDefault();
    if (!reg.trim() || !mobile.trim()) {
      toast.error("Enter both Vehicle Reg No. and Mobile Number");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/public/warranty-request-otp", {
        vehicle_reg_no: reg.trim(), mobile: mobile.trim(),
      });
      setOtpSent(true);
      if (r.data.debug_otp) {
        setDebugOtp(r.data.debug_otp);
        toast.warning("WhatsApp preview failed — dev OTP shown below.");
      } else {
        toast.success("OTP sent on WhatsApp");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No warranty found");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (otp.length < 4) { toast.error("Enter OTP"); return; }
    setLoading(true);
    try {
      const r = await api.post("/public/warranty-verify-otp", {
        vehicle_reg_no: reg.trim(), mobile: mobile.trim(), code: otp,
      });
      setResult(r.data);
      toast.success("Verified!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const resetLookup = () => { setResult(null); setOtpSent(false); setOtp(""); setDebugOtp(null); };

  const dlPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/warranty/${result.warranty_id}/certificate.pdf?token=${encodeURIComponent(result.download_token)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Logo size="md" />
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#475569]">
            <a href="#verify" className="hover:text-slate-900">Verify Warranty</a>
            <a href="#products" className="hover:text-slate-900">Products</a>
            <a href="#how" className="hover:text-slate-900">How it Works</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/register-distributor"
              data-testid="nav-register-distributor"
              className="hidden sm:inline text-sm text-[#475569] hover:text-slate-900"
            >
              Become Distributor
            </Link>
            <Link
              to="/login"
              data-testid="nav-login"
              className="text-sm px-4 py-2 rounded-md bg-[#E63946] hover:bg-[#D32F2F] text-white"
            >
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 cpl-grid opacity-40" />
        <div className="absolute inset-0 cpl-glow" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/11908415/pexels-photo-11908415.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="anim-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E63946]/10 border border-[#E63946]/30 text-[11px] uppercase tracking-[0.2em] text-[#E63946] mb-6">
              <Sparkles size={12} /> Authorized Warranty Portal
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05]">
              Unmatched protection<br />
              <span className="text-[#E63946]">for your car,</span><br />
              guaranteed by Chiripal.
            </h1>
            <p className="mt-6 text-lg text-[#475569] max-w-xl leading-relaxed">
              Premium Paint Protection Film, designed for lasting durability and superior aesthetics. Verify your warranty, download your certificate, and track coverage — all in one place.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-[#475569]">
              <div className="flex items-center gap-2"><BadgeCheck size={16} className="text-[#10B981]" /> Self-healing PPF</div>
              <div className="flex items-center gap-2"><BadgeCheck size={16} className="text-[#10B981]" /> Up to lifetime warranty</div>
              <div className="flex items-center gap-2"><BadgeCheck size={16} className="text-[#10B981]" /> Authorized dealer network</div>
            </div>
          </div>

          {/* Check warranty card */}
          <div id="verify" className="anim-up" style={{ animationDelay: ".15s" }}>
            <div className="rounded-2xl border border-[#E5E7EB] bg-[#FFFFFF]/80 backdrop-blur-xl p-7 shadow-2xl">
              <div className="flex items-center gap-3 mb-1">
                <div className="h-10 w-10 rounded-md bg-[#E63946]/10 border border-[#E63946]/30 flex items-center justify-center">
                  <ShieldCheck size={18} className="text-[#E63946]" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#64748B]">Customer Portal</div>
                  <div className="text-xl font-medium">Verify Warranty</div>
                </div>
              </div>
              <p className="text-sm text-[#475569] mt-2">
                Enter your vehicle registration number and mobile number to look up your active warranty.
              </p>
              <form onSubmit={otpSent && !result ? verify : check} className="mt-6 space-y-4">
                {!otpSent && (<>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Vehicle Reg No.</label>
                  <div className="relative mt-1">
                    <Car size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                    <input
                      data-testid="warranty-reg-input"
                      value={reg}
                      onChange={(e) => setReg(e.target.value.toUpperCase())}
                      placeholder="GJ01AB1234"
                      className="w-full pl-9 pr-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md font-mono text-slate-900 focus:outline-none focus:border-[#E63946]"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Mobile Number</label>
                  <input
                    data-testid="warranty-mobile-input"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="10-digit mobile"
                    className="mt-1 w-full px-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]"
                  />
                </div>
                <button
                  data-testid="warranty-verify-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? "Sending OTP..." : (<><Search size={16} /> Send OTP</>)}
                </button>
                </>)}
                {otpSent && !result && (<>
                  {debugOtp && (
                    <div className="px-3 py-2 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#B45309] text-xs" data-testid="public-debug-otp">
                      Dev OTP: <span className="font-mono font-semibold">{debugOtp}</span>
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Enter 6-digit OTP sent to +91 {mobile}</label>
                    <input
                      data-testid="public-otp-input"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="000000"
                      className="mt-1 w-full px-3 py-3 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md font-mono text-2xl tracking-[0.5em] text-center text-slate-900 focus:outline-none focus:border-[#E63946]"
                      autoFocus
                    />
                  </div>
                  <button
                    data-testid="public-verify-otp-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md font-medium disabled:opacity-60"
                  >
                    {loading ? "Verifying..." : "Verify & View Warranty"}
                  </button>
                  <button type="button" onClick={resetLookup} className="w-full text-xs text-[#64748B] hover:text-slate-900">Change details</button>
                </>)}
              </form>

              {result && (
                <div className="mt-6 rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-5 anim-up" data-testid="warranty-result-card">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#64748B]">Warranty</div>
                    <span
                      className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${
                        result.status === "active" ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#64748B]/20 text-[#475569]"
                      }`}
                      data-testid="warranty-status"
                    >
                      {result.status}
                    </span>
                  </div>
                  <div className="text-lg font-mono text-[#E63946] mt-1">{result.warranty_number}</div>
                  <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                    <KV k="Product" v={result.product_name} />
                    <KV k="Category" v={result.product_category} />
                    <KV k="Vehicle" v={`${result.vehicle?.brand || ""} ${result.vehicle?.model || ""}`.trim()} />
                    <KV k="Reg. No." v={result.vehicle?.reg_no} mono />
                    <KV k="Issued" v={fmtDate(result.issued_at)} />
                    <KV k="Expires" v={result.is_lifetime ? "Lifetime" : fmtDate(result.expires_at)} />
                    <KV k="Dealer" v={result.dealer?.name} />
                    <KV k="Dealer City" v={result.dealer?.address_city} />
                  </div>
                  {result.is_capped_by_registration && (
                    <div className="mt-3 text-[11px] text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded p-2" data-testid="warranty-capped-note">
                      * Warranty expiry has been capped to your vehicle's registration expiry ({fmtDate(result.vehicle?.registration_expiry)}).
                    </div>
                  )}
                  {result.ppf_usages && result.ppf_usages.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mb-2">PPF Rolls</div>
                      <div className="space-y-1">
                        {result.ppf_usages.map((u, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="font-mono text-[#E63946]">{u.ppf_code}</span>
                            <span className="text-[#475569] font-mono">{u.sqft_used} SqFt / {u.roll_size_sqft} SqFt</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <button
                    data-testid="warranty-download-pdf"
                    onClick={dlPdf}
                    className="mt-5 w-full py-2.5 border border-[#E63946] text-[#E63946] hover:bg-[#E63946]/10 rounded-md text-sm font-medium flex items-center justify-center gap-2"
                  >
                    Download Certificate (PDF) <ArrowRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-24 border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#E63946] mb-3">Products</div>
          <h2 className="text-3xl lg:text-4xl tracking-tight font-medium">A film for every drive.</h2>
          <p className="text-[#475569] mt-3 max-w-2xl">From flagship lifetime gloss to color-changing satin, every Chiripal PPF roll is engineered for clarity, scratch resistance, and self-healing performance.</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { cat: "King Of Gloss", name: "UltraMax PPF", w: "Lifetime", flag: true },
              { cat: "King Of Gloss", name: "ElitePro PPF", w: "8 years" },
              { cat: "King Of Gloss", name: "ProMax PPF", w: "7 years" },
              { cat: "King Of Gloss", name: "Pro PPF", w: "5 years" },
              { cat: "Ultimate Satin", name: "Clear Satin PPF", w: "7 years" },
              { cat: "Color PPF", name: "Gloss Black PPF", w: "5 years" },
              { cat: "Color PPF", name: "Satin Black PPF", w: "5 years" },
            ].map((p, i) => (
              <div key={i} className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-5 hover:border-[#E63946]/40 transition">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B]">{p.cat}</div>
                <div className="text-lg font-medium mt-2">{p.name}</div>
                <div className="mt-4 flex items-center justify-between">
                  <div className={`text-sm ${p.flag ? "text-[#E63946]" : "text-[#475569]"}`}>{p.w}</div>
                  <ShieldCheck size={16} className={p.flag ? "text-[#E63946]" : "text-[#64748B]"} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="py-24 border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
          {[
            { t: "1. Buy from dealer", d: "Visit an authorized Chiripal dealer to install PPF on your vehicle." },
            { t: "2. Get warranty", d: "Your dealer scans the unique PPF code & activates a system-generated warranty linked to your vehicle." },
            { t: "3. Verify anytime", d: "Enter your vehicle reg & mobile number above to view and download your certificate." },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-8">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#E63946]">Step {i + 1}</div>
              <div className="text-xl font-medium mt-2">{s.t}</div>
              <p className="text-[#475569] mt-3 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4">
          <Logo size="sm" />
          <div className="flex items-center gap-2 text-xs text-[#64748B]"><span>© Chiripal Poly Films Ltd.Paint Protection Film.</span><a href="https://compubrain.com" style={{position: "relative",top: "-6px"}} target="_blank" rel="noopener noreferrer"><img className="footer-logo h-6 w-auto" src="compubrain.png"alt="CompuBrain"/></a></div>
        </div>
      </footer>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; }
}

function KV({ k, v, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B]">{k}</div>
      <div className={`mt-0.5 text-slate-900 ${mono ? "font-mono" : ""}`}>{v || "-"}</div>
    </div>
  );
}
